// @ts-nocheck
"use client";

import * as React from "react";
import { Button, Flex, Text, Box, Icon, IconButton } from "@chakra-ui/react";
import { LuFilePlus, LuUpload, LuX } from "react-icons/lu";
import { ChatService } from "@/client";
import useCustomToast from "@/hooks/useCustomToast";

interface FileUploadProps {
  onFileUpload: (fileId: string, fileName: string) => void;
  isUploading: boolean;
  onUploadingChange: (uploading: boolean) => void;
  disabled?: boolean;
}

export const FileUpload = ({
  onFileUpload,
  isUploading,
  onUploadingChange,
  disabled = false,
}: FileUploadProps) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const { showSuccessToast, showErrorToast } = useCustomToast();

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // 파일 크기 검증 (50MB 제한)
    if (file.size > 50 * 1024 * 1024) {
      showErrorToast("파일 크기가 50MB를 초과합니다.");
      return;
    }

    // 지원하는 파일 형식 검증
    const supportedTypes = [
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "text/plain",
      "text/markdown",
      // 이미지 타입 추가
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
    ];

    if (!supportedTypes.includes(file.type)) {
      showErrorToast(`지원하지 않는 파일 형식입니다: ${file.type}`);
      return;
    }

    onUploadingChange(true);
    try {
      const response = await ChatService.uploadDocument({
        formData: { file },
      });

      if (response?.file_id) {
        const fileId = response.file_id as string;
        const fileName = (response.filename as string) || file.name;
        onFileUpload(fileId, fileName);
        showSuccessToast(`${file.name}이 성공적으로 업로드되었습니다.`);
      }
    } catch (error) {
      console.error("파일 업로드 실패:", error);
      showErrorToast("파일 업로드 중 오류가 발생했습니다.");
    } finally {
      onUploadingChange(false);
      // 파일 입력 초기화
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  return (
    <Flex justify="flex-start">
      <Button
        aria-label="파일 첨부"
        variant="outline"
        colorScheme="blue"
        size="sm"
        onClick={() => fileInputRef.current?.click()}
        disabled={isUploading || disabled}
      >
        {isUploading ? "⏳" : <Icon as={LuFilePlus} />}
      </Button>

      {/* 숨겨진 파일 입력 */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.docx,.txt,.md,.jpg,.jpeg,.png,.gif,.webp"
        onChange={handleFileUpload}
        style={{ display: "none" }}
      />
    </Flex>
  );
};

// FileUploadRoot 컴포넌트
interface FileUploadRootProps {
  children: React.ReactNode;
  maxW?: string;
  alignItems?: string;
  maxFiles?: number;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
}

export const FileUploadRoot: React.FC<FileUploadRootProps> = ({
  children,
  maxW,
  alignItems,
  maxFiles,
  inputProps,
}) => {
  const [files, setFiles] = React.useState<File[]>([]);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);

    if (maxFiles && files.length + selectedFiles.length > maxFiles) {
      return;
    }

    setFiles((prev) => [...prev, ...selectedFiles]);

    // inputProps의 onChange 호출
    if (inputProps?.onChange) {
      inputProps.onChange(event);
    }
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Box maxW={maxW} w="100%">
      <input
        ref={fileInputRef}
        type="file"
        multiple={maxFiles ? maxFiles > 1 : false}
        onChange={handleFileChange}
        style={{ display: "none" }}
        {...inputProps}
      />
      <Box
        onClick={() => fileInputRef.current?.click()}
        cursor="pointer"
        alignItems={alignItems}
      >
        {children}
      </Box>
    </Box>
  );
};

// FileUploadDropzone 컴포넌트
interface FileUploadDropzoneProps {
  label: string;
  description?: string;
}

export const FileUploadDropzone: React.FC<FileUploadDropzoneProps> = ({
  label,
  description,
}) => {
  return (
    <Box
      border="2px dashed"
      borderColor="gray.300"
      borderRadius="md"
      p={6}
      textAlign="center"
      _hover={{
        borderColor: "blue.400",
        bg: "blue.50",
      }}
      transition="all 0.2s"
    >
      <Icon as={LuUpload} fontSize="2xl" color="gray.400" mb={2} />
      <Text fontWeight="medium" color="gray.700" mb={1}>
        {label}
      </Text>
      {description && (
        <Text fontSize="sm" color="gray.500">
          {description}
        </Text>
      )}
    </Box>
  );
};

// FileUploadList 컴포넌트
interface FileUploadListProps {
  showSize?: boolean;
  files?: File[];
  onRemove?: (index: number) => void;
}

export const FileUploadList: React.FC<FileUploadListProps> = ({
  showSize = false,
  files = [],
  onRemove,
}) => {
  if (files.length === 0) return null;

  return (
    <Box mt={4}>
      {files.map((file, index) => (
        <Flex
          key={`${file.name}-${index}`}
          align="center"
          justify="space-between"
          p={2}
          bg="gray.50"
          borderRadius="md"
          mb={2}
        >
          <Flex align="center" flex={1}>
            <Icon as={LuFilePlus} fontSize="sm" color="gray.500" mr={2} />
            <Box flex={1}>
              <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                {file.name}
              </Text>
              {showSize && (
                <Text fontSize="xs" color="gray.500">
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </Text>
              )}
            </Box>
          </Flex>
          <IconButton
            aria-label="파일 제거"
            icon={<LuX />}
            size="sm"
            variant="ghost"
            colorScheme="red"
            onClick={() => onRemove?.(index)}
          />
        </Flex>
      ))}
    </Box>
  );
};
